import { A, useNavigate } from "@solidjs/router";
import type { IconTypes } from "solid-icons";
import { AiOutlinePlusCircle } from "solid-icons/ai";
import { FiChevronDown, FiChevronLeft } from "solid-icons/fi";
import {
  IoBriefcaseOutline,
  IoBusinessOutline,
  IoLogOutOutline,
  IoPersonOutline,
  IoSearchOutline,
} from "solid-icons/io";
import { VsLinkExternal } from "solid-icons/vs";
import {
  type Component,
  createMemo,
  createSignal,
  Match,
  type ParentComponent,
  Show,
  Switch,
} from "solid-js";
import { produce } from "solid-js/store";
import { removeAuthToken } from "@/api/client";
import { useI18n } from "@/api/i18n";
import { useAuthV2 } from "@/domains/auth/context/auth-provider";
import { useOptionalBillingContext } from "@/domains/billing/context/billing-provider";
import {
  clearCompanyStore,
  useCompanyStore,
} from "@/domains/company/company/store/company-store";
import { confirmLeavingUnsavedWork } from "@/domains/company/company/store/unsaved-work";
import { clearAllCompanyOnboardingDrafts } from "@/domains/company/onboarding/store/draft-store";
import type { ProfileV2 } from "@/domains/freelance/profile/api/types";
import { clearLegacyJobOfferDrafts } from "@/domains/job-offer/create/utils/legacy-drafts";
import { usePostHog } from "@/hooks/usePostHog";
import { cn } from "@/libs/cn";
import { useLayoutContext } from "../layout/context/LayoutContext";
import { Button, buttonVariants } from "../ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";

/**
 * One timing for the whole desktop collapse. The aside's width, the nav's
 * padding, the section gaps, the titles' height and every label's opacity all
 * run on exactly this, so the panel reads as a single object narrowing rather
 * than a pile of independently timed parts. `MainContentContainer`'s margin at
 * the bottom of this file is the same 300ms curve for the same reason.
 *
 * No `lg:` prefix is needed anywhere: `isSidebarCollapsed()` is already gated on
 * the breakpoint (see `LayoutContext`), so none of these properties ever change
 * below `lg` and the transitions simply never fire there. That also keeps us
 * clear of Tailwind v4's variant ordering, where a `lg:` transition would need
 * its own `lg:motion-reduce:` twin to stay overridable.
 *
 * `ease-drawer` rather than `ease-in-out`: the built-in curve is
 * `cubic-bezier(0.4, 0, 0.2, 1)`, which idles through the first ~80ms — the exact
 * window the user is watching. `--ease-drawer` front-loads the movement and is
 * already what the same `<aside>` uses for its mobile slide-out, so the panel
 * stops having two personalities depending on the viewport.
 *
 * Reduced motion narrows the transition to `opacity` instead of killing it
 * outright: the standard asks for gentler, not zero — fades and colour changes
 * carry meaning and stay, movement goes. Because every animated property here is
 * either opacity or layout, that one variant is correct on all of them: labels
 * still fade, widths and margins snap.
 */
const COLLAPSE_MOTION =
  "duration-300 ease-drawer motion-reduce:transition-[opacity]";

export const Sidebar: ParentComponent = (props) => {
  const {
    isMobileMenuOpen,
    isSidebarCollapsed,
    toggleSidebarCollapsed,
    isDesktop,
  } = useLayoutContext();
  /**
   * Below `lg` this aside is an off-canvas drawer. Closed, it only slides out
   * of sight at `-translate-x-full` — every link inside stays in the tab order
   * and stays visible to screen readers, so a keyboard user tabs through a menu
   * they cannot see. `inert` removes it from both.
   *
   * From `lg` up the same element is the permanent sidebar (`lg:translate-x-0`)
   * and has to stay reachable, hence the breakpoint check: `inert` is an
   * attribute, so there is no `lg:` variant to lean on.
   */
  const isDrawerHidden = () => !isDesktop() && !isMobileMenuOpen();
  return (
    <aside
      id="mobile-menu"
      inert={isDrawerHidden() || undefined}
      class="fixed inset-y-0 left-0 lg:top-14 top-12 z-20 bg-white shadow-lg lg:shadow-none transition-[translate] duration-200 ease-drawer lg:transition-[width] lg:duration-300 lg:ease-drawer motion-reduce:transition-none lg:motion-reduce:transition-none"
      classList={{
        "translate-x-0": isMobileMenuOpen(),
        "-translate-x-full lg:translate-x-0": !isMobileMenuOpen(),
        "w-64": !isSidebarCollapsed(),
        "w-16": isSidebarCollapsed(),
      }}
    >
      <button
        type="button"
        onClick={toggleSidebarCollapsed}
        class="hidden lg:flex absolute -right-3 top-6 w-6 h-6 bg-white border border-gray-200 rounded-full items-center justify-center shadow-sm hover:bg-gray-50 transition-colors z-10"
        aria-label={
          isSidebarCollapsed() ? "Expand sidebar" : "Collapse sidebar"
        }
      >
        {/* The affordance, not the panel: it reports the state change rather
            than travelling with it, so it snaps at 200ms instead of riding the
            300ms collapse. Pure rotation, no opacity — reduced motion drops it
            entirely rather than narrowing to a fade that isn't there. */}
        <FiChevronLeft
          class="w-3.5 h-3.5 text-gray-500 transition-transform duration-200 ease-drawer motion-reduce:transition-none"
          classList={{ "rotate-180": isSidebarCollapsed() }}
        />
      </button>
      <div class="flex flex-col h-full border-r border-gray-200 overflow-hidden">
        {props.children}
      </div>
    </aside>
  );
};

/**
 * The scrollable body of the sidebar. It owns the horizontal padding because
 * that padding is what centres the icons in the collapsed rail: 10px here plus
 * each item's own 12px puts a 20px glyph at 22px from either edge of the 64px
 * rail. Expanded it relaxes back to 16px. The 6px difference is transitioned
 * rather than snapped, so the icons drift into place with the width instead of
 * stepping sideways on the first frame.
 */
export const SidebarNav: ParentComponent<{ class?: string }> = (props) => {
  const { isSidebarCollapsed } = useLayoutContext();
  return (
    <nav
      class={cn(
        "flex-1 py-6 transition-[padding]",
        COLLAPSE_MOTION,
        props.class,
      )}
      classList={{
        "px-4": !isSidebarCollapsed(),
        "px-2.5": isSidebarCollapsed(),
      }}
    >
      {props.children}
    </nav>
  );
};

/**
 * A group of nav items. The gap above it halves when the rail collapses: with
 * the titles gone, 32px of air between four groups of anonymous glyphs is what
 * makes the collapsed sidebar read as leftover space rather than as a deliberate
 * icon strip.
 */
export const SidebarSection: ParentComponent<{ class?: string }> = (props) => {
  const { isSidebarCollapsed } = useLayoutContext();
  return (
    <div
      class={cn("transition-[margin]", COLLAPSE_MOTION, props.class)}
      classList={{
        "mt-8": !isSidebarCollapsed(),
        "mt-4": isSidebarCollapsed(),
      }}
    >
      {props.children}
    </div>
  );
};

/**
 * A label that survives the collapse. The previous `<Show>` deleted the text on
 * the first frame, leaving the panel to spend the next 300ms as an empty box;
 * keeping it mounted lets it fade while the row's own `overflow-hidden` wipes it
 * away from the right. It also keeps the link's accessible name intact once
 * collapsed, which the `title` attribute was standing in for before.
 */
const SidebarLabel: ParentComponent<{ class?: string }> = (props) => {
  const { isSidebarCollapsed } = useLayoutContext();
  return (
    <span
      class={cn(
        "font-medium whitespace-nowrap transition-opacity",
        COLLAPSE_MOTION,
        props.class,
      )}
      classList={{ "opacity-0": isSidebarCollapsed() }}
    >
      {props.children}
    </span>
  );
};

/**
 * The collapsed rail is unreadable without this: an item is a glyph and nothing
 * else. Kobalte is told to stand down with `disabled` rather than wrapped in a
 * `<Show>`, so toggling the sidebar never remounts the trigger and never drops
 * focus mid-collapse. Placed to the right because the rail is pinned to the left
 * edge of the viewport.
 *
 * The gutter is measured off the row, not off the rail: a row stops 11px short of
 * the rail's edge, so the default 4px would leave the tooltip sitting *on* the
 * sidebar's border. 16 clears it with room to spare.
 */
const SidebarItemTooltip: ParentComponent<{ label: string }> = (props) => {
  const { isSidebarCollapsed } = useLayoutContext();
  return (
    <Tooltip
      placement="right"
      gutter={16}
      openDelay={200}
      disabled={!isSidebarCollapsed()}
    >
      {props.children}
      <TooltipContent>{props.label}</TooltipContent>
    </Tooltip>
  );
};

type SidebarNavigationItemProps = {
  name: string;
  icon: IconTypes;
  active: boolean;
  href: string;
};

export const SidebarNavigationItem = (props: SidebarNavigationItemProps) => {
  return (
    <SidebarItemTooltip label={props.name}>
      <TooltipTrigger as={A} href={props.href}>
        <div
          class="flex items-center gap-3 px-3 py-2 rounded-lg transition-colors text-muted-foreground text-sm overflow-hidden"
          classList={{
            "bg-blue-50 text-primary border-r-2 border-primary": props.active,
            "text-gray-700 hover:bg-gray-100": !props.active,
          }}
        >
          <props.icon class="w-5 h-5 shrink-0" />
          <SidebarLabel>{props.name}</SidebarLabel>
        </div>
      </TooltipTrigger>
    </SidebarItemTooltip>
  );
};

export const ExternalNavigationItem = (props: SidebarNavigationItemProps) => {
  const { isSidebarCollapsed } = useLayoutContext();
  return (
    <SidebarItemTooltip label={props.name}>
      {/* The link itself is styled as the button: a `Button` inside it would
          be a second tab stop for the same control. */}
      <TooltipTrigger
        as={A}
        href={props.href}
        // `padding` joins the button's own transition list (and so runs at its
        // 160ms rather than the sidebar's 300ms — it is a 2px move). The 14px
        // is not arbitrary: this button's 16px would leave its icon 2px right
        // of the axis the other eight icons sit on, because the row's content
        // overflows and the icon ends up pinned to the *left* padding rather
        // than the right edge it appears to follow while expanded.
        class={cn(
          buttonVariants({ variant: "secondary", size: "lg" }),
          "w-full justify-between hover:bg-secondary/80 text-muted-foreground hover:text-secondary-foreground overflow-hidden transition-[color,background-color,box-shadow,scale,padding] motion-reduce:transition-[color,background-color]",
          isSidebarCollapsed() && "px-3.5",
        )}
      >
        {/* Unlike every other item this one's icon sits on the *right*, so it
            has to ride the shrinking edge instead of staying put. `min-w-0`
            is what lets the label give up its width smoothly — without it the
            nowrap text holds the row open and shoves the icon out of sight. */}
        <SidebarLabel class="flex-1 min-w-0 overflow-hidden text-left">
          {props.name}
        </SidebarLabel>
        <VsLinkExternal class="w-4 h-4 shrink-0" />
      </TooltipTrigger>
    </SidebarItemTooltip>
  );
};

type SidebarActionItemProps = {
  name: string;
  icon: IconTypes;
  onClick: () => void;
};

/**
 * The non-navigating rows (support, donation, invite). They used to be hand-
 * written at each call site in both layouts, which is how five copies of the
 * same collapse behaviour drifted apart; the collapse now lives here only.
 */
export const SidebarActionItem = (props: SidebarActionItemProps) => {
  return (
    <SidebarItemTooltip label={props.name}>
      <TooltipTrigger
        as={Button}
        variant="ghost"
        size="default"
        class="w-full justify-start px-3 overflow-hidden text-sm text-muted-foreground"
        onClick={props.onClick}
      >
        <props.icon class="w-5 h-5 shrink-0 mr-2" />
        <SidebarLabel>{props.name}</SidebarLabel>
      </TooltipTrigger>
    </SidebarItemTooltip>
  );
};

/**
 * Collapses to nothing instead of being unmounted, so the rows below it glide up
 * with the width rather than snapping up on the first frame. `whitespace-nowrap`
 * stops a long title wrapping to two or three lines while the rail is still
 * narrow — which is what made the titles visibly re-flow on the way back open.
 *
 * `h-8` is the 16px line box `text-xs` produces plus the 16px of air that used to
 * be a `mb-4`. Folding the margin into the box is not cosmetic: `height` and
 * `margin-bottom` were two separate layout transitions per title, and there are
 * three titles per sidebar. Same pixels, three fewer relayouts per frame.
 */
export const SidebarNavigationTitle: ParentComponent = (props) => {
  const { isSidebarCollapsed } = useLayoutContext();
  return (
    <div
      class={cn(
        "text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap overflow-hidden transition-[height,opacity]",
        COLLAPSE_MOTION,
      )}
      classList={{
        "h-8": !isSidebarCollapsed(),
        "h-0 opacity-0": isSidebarCollapsed(),
      }}
    >
      {props.children}
    </div>
  );
};

export const MobileTopBar: ParentComponent = (props) => {
  return (
    <div class="lg:hidden fixed top-0 z-30 w-full bg-white shadow-sm border-b">
      <div class="flex items-center justify-between px-4 py-3">
        {props.children}
      </div>
    </div>
  );
};

export const MobileOverlay: Component = () => {
  const { isMobileMenuOpen, toggleMobileMenu } = useLayoutContext();
  return (
    <button
      type="button"
      class="lg:hidden fixed inset-0 bg-black/50 z-20 transition-opacity duration-200 ease-drawer motion-reduce:transition-none"
      classList={{
        "opacity-100": isMobileMenuOpen(),
        "opacity-0 pointer-events-none": !isMobileMenuOpen(),
      }}
      inert={!isMobileMenuOpen() || undefined}
      aria-label="Close mobile menu"
      aria-expanded={isMobileMenuOpen()}
      aria-controls="mobile-menu"
      onClick={toggleMobileMenu}
    />
  );
};

export const DesktopTopBar: ParentComponent = (props) => {
  return (
    <div class="hidden bg-white lg:flex items-center justify-between gap-3 px-6 py-4 border-b fixed top-0 z-30 w-full">
      {props.children}
    </div>
  );
};

type AppSwitcherPopoverProps = {
  type: ProfileV2["profile_type"];
};

export const AppSwitcherPopover = (props: AppSwitcherPopoverProps) => {
  const [isPopoverOpen, setIsPopoverOpen] = createSignal(false);
  const { reset } = usePostHog();
  const navigate = useNavigate();
  const [authStoreV2, setAuthStoreV2] = useAuthV2();
  const userProfile = createMemo(() => authStoreV2?.profile);
  const [companyStore] = useCompanyStore();
  const { t } = useI18n();
  const { isSidebarCollapsed } = useLayoutContext();
  const billingCtx = useOptionalBillingContext();
  const isReadOnly = () =>
    billingCtx?.billingStatusContract()?.billing_status === "unpaid";
  const canPublish = () => authStoreV2?.capabilities?.can_publish === true;
  const logout = async () => {
    // The fourth way off a page holding unsaved work, and the only one that
    // takes the session with it. Everything below this line is irreversible —
    // the token goes, the company store goes — so the question has to be asked
    // *before* the first of them: a dialog opened afterwards would offer to
    // save through a session that no longer exists, and "salva ed esci" would
    // fail with the user trapped on a page they can neither save nor leave
    // (AC 26). Same shape as `useSelectWorkspace`; on every other page the
    // registry is empty and this resolves immediately.
    if (!(await confirmLeavingUnsavedWork())) return;

    if (setAuthStoreV2)
      setAuthStoreV2(
        produce((state) => {
          state.profile = null;
          state.companies = null;
          state.user = null;
          state.user_applications = null;
        }),
      );
    removeAuthToken();
    clearCompanyStore();
    clearLegacyJobOfferDrafts();
    clearAllCompanyOnboardingDrafts();
    reset();
    navigate("/auth");
  };
  return (
    <Popover
      gutter={2}
      flip
      open={isPopoverOpen()}
      onOpenChange={setIsPopoverOpen}
    >
      <PopoverTrigger>
        {/* The 16px padding is left alone on collapse on purpose: it already
            centres the 32px avatar exactly in the 64px rail, so this row needs
            no geometry of its own — only the text has to go. */}
        <div class="px-4 py-4 border-t border-gray-200 cursor-pointer hover:bg-gray-50 rounded-lg transition-colors overflow-hidden">
          <div class="flex items-center gap-3">
            <div class="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center shrink-0">
              <IoPersonOutline class="w-5 h-5 text-gray-600" />
            </div>
            <div
              class={cn("flex-1 min-w-0 transition-opacity", COLLAPSE_MOTION)}
              classList={{ "opacity-0": isSidebarCollapsed() }}
            >
              <p class="text-sm font-medium text-gray-900 truncate text-left">
                {userProfile()?.full_name || t("profile.layout.userFallback")}
              </p>
              <Show
                when={props.type === "provider"}
                fallback={
                  <p class="text-xs text-gray-500 text-left">
                    {t("profile.layout.personalSpace")}
                  </p>
                }
              >
                <p class="text-xs text-gray-500 text-left">
                  {t("profile.layout.recruiterSpace")}
                </p>
              </Show>
            </div>
            <FiChevronDown
              // `rotate-180` compiles to the `rotate` longhand in Tailwind v4,
              // not to `transform` — naming the wrong property here would
              // silently kill the chevron's flip.
              class={cn(
                "w-4 h-4 shrink-0 text-gray-500 transition-[rotate,opacity]",
                COLLAPSE_MOTION,
              )}
              classList={{
                "rotate-180": isPopoverOpen(),
                "opacity-0": isSidebarCollapsed(),
              }}
            />
          </div>
        </div>
      </PopoverTrigger>
      <PopoverContent hideCloseButton class="w-fit max-w-60">
        <div class="flex flex-col gap-2">
          <Button
            variant="ghost"
            onClick={logout}
            class="w-full justify-start text-muted-foreground"
          >
            <IoLogOutOutline class="w-5 h-5 mr-2" />
            {t("common.logout")}
          </Button>
          <Switch>
            <Match when={props.type === "consumer"}>
              <Show
                when={companyStore?.selectedCompany && canPublish()}
                fallback={
                  <Tooltip openDelay={100} disabled={!isReadOnly()}>
                    <TooltipTrigger as="div" class="w-full">
                      <Button
                        variant="ghost"
                        class="w-full justify-start text-muted-foreground"
                        onClick={() =>
                          !isReadOnly() && navigate("/job-offers/create/start")
                        }
                        disabled={isReadOnly()}
                        aria-disabled={isReadOnly()}
                      >
                        <AiOutlinePlusCircle class="w-5 h-5 mr-2" />
                        {t("layout.nav.publishOffer")}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {t("billing.read_only_tooltip")}
                    </TooltipContent>
                  </Tooltip>
                }
              >
                <Button
                  variant="ghost"
                  onClick={() => navigate("/profile/company")}
                  class="w-full justify-start text-muted-foreground"
                >
                  <IoBusinessOutline class="w-5 h-5 mr-2" />
                  {t("layout.nav.recruiterDashboard")}
                </Button>
              </Show>
            </Match>
            <Match when={props.type === "provider"}>
              <Show
                when={companyStore?.selectedCompany?.kind === "company"}
                fallback={
                  <Button
                    variant="ghost"
                    class="w-full justify-start text-muted-foreground"
                    onClick={() => navigate("/profile")}
                  >
                    <IoBriefcaseOutline class="w-5 h-5 mr-2" />
                    {t("layout.nav.freelanceApp")}
                  </Button>
                }
              >
                <Button
                  variant="ghost"
                  class="w-full justify-start text-muted-foreground"
                  onClick={() => navigate("/")}
                >
                  <IoSearchOutline class="w-5 h-5 mr-2" />
                  {t("layout.nav.exploreOffers")}
                </Button>
              </Show>
            </Match>
          </Switch>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export const MainContentContainer: ParentComponent = (props) => {
  const { isSidebarCollapsed } = useLayoutContext();
  return (
    <main
      /**
       * `min-w-0`: come flex item questo `<main>` ha `min-width: auto`, quindi
       * non scende mai sotto il min-content del contenuto — invece di comprimersi
       * allarga l'intero documento oltre il viewport. Il sintomo non è il taglio
       * del contenuto ma il navbar: `MobileTopBar` è `fixed w-full`, si dimensiona
       * sul documento allargato e finisce fuori dallo schermo, disallineato dal
       * footer. Con `min-w-0` la larghezza torna definita e i `max-w-full` /
       * `minmax(0,…)` che i figli già dichiarano hanno finalmente qualcosa
       * contro cui restringersi.
       */
      class={cn(
        "flex-1 min-w-0 pt-20 lg:pt-24 pb-6 min-h-screen transition-[margin]",
        COLLAPSE_MOTION,
      )}
      classList={{
        "lg:ml-64": !isSidebarCollapsed(),
        "lg:ml-16": isSidebarCollapsed(),
      }}
    >
      {props.children}
    </main>
  );
};
